/**
 * Founder Retention Dashboard - Admin Only
 * 
 * Behavioral stability metrics for founders.
 * Aggregated data only, no PII, ARC Doctrine compliant.
 */
import { useState, useEffect, useCallback } from "react";
import { AppLayout } from "./DashboardPage";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { 
  BarChart3, 
  Target, 
  TrendingUp, 
  Users, 
  Calendar,
  ArrowRight,
  CheckCircle2,
  Activity,
  Loader2,
  Shield,
  Clock
} from "lucide-react";
import api from "../lib/api";

// ============== Components ==============

function MetricCard({ title, value, subtitle, icon: Icon, trend, color = "emerald" }) {
  const colorClasses = {
    emerald: "text-emerald-600 bg-emerald-50",
    blue: "text-blue-600 bg-blue-50",
    purple: "text-purple-600 bg-purple-50",
    amber: "text-amber-600 bg-amber-50",
    gray: "text-gray-600 bg-gray-50",
  };
  
  return (
    <Card data-testid={`metric-card-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      <CardContent className="pt-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm text-muted-foreground">{title}</p>
            <p className="text-2xl font-bold mt-1">{value}</p>
            {subtitle && (
              <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>
            )}
          </div>
          {Icon && (
            <div className={`p-2 rounded-lg ${colorClasses[color]}`}>
              <Icon className="w-5 h-5" />
            </div>
          )}
        </div>
        {trend !== undefined && (
          <div className="mt-3 flex items-center gap-1 text-sm">
            <TrendingUp className="w-4 h-4 text-emerald-500" />
            <span className="text-emerald-600">{trend}</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function FunnelBar({ stages, total }) {
  if (!stages || !total) return null;
  
  return (
    <div className="space-y-3" data-testid="funnel-bar">
      {Object.entries(stages).map(([stage, count], index) => {
        const percent = total > 0 ? (count / total * 100) : 0;
        const label = stage.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
        
        return (
          <div key={stage} className="space-y-1">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">{label}</span>
              <span className="font-medium">{count} ({percent.toFixed(0)}%)</span>
            </div>
            <Progress value={percent} className="h-2" />
          </div>
        );
      })}
    </div>
  );
}

function DistributionBar({ distribution }) {
  if (!distribution || Object.keys(distribution).length === 0) {
    return <p className="text-sm text-muted-foreground">No data available</p>;
  }
  
  const total = Object.values(distribution).reduce((a, b) => a + b, 0);
  const colors = ["bg-emerald-500", "bg-emerald-400", "bg-emerald-300", "bg-emerald-200", "bg-emerald-100"];
  
  return (
    <div className="space-y-2" data-testid="distribution-bar">
      <div className="flex h-6 rounded-full overflow-hidden">
        {Object.entries(distribution).map(([key, value], index) => {
          const percent = total > 0 ? (value / total * 100) : 0;
          if (percent === 0) return null;
          
          return (
            <div 
              key={key}
              className={`${colors[index % colors.length]}`}
              style={{ width: `${percent}%` }}
              title={`${key}: ${value} (${percent.toFixed(0)}%)`}
            />
          );
        })}
      </div>
      <div className="flex flex-wrap gap-3 text-sm">
        {Object.entries(distribution).map(([key, value], index) => (
          <div key={key} className="flex items-center gap-1">
            <div className={`w-3 h-3 rounded-full ${colors[index % colors.length]}`} />
            <span>{key} weeks: {value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============== Section Components ==============

function WeeklyCloseHealth({ data }) {
  if (!data) return null;
  
  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <MetricCard 
          title="7-Day Completion" 
          value={`${data.wccr_7d}%`}
          icon={CheckCircle2}
          color="emerald"
        />
        <MetricCard 
          title="30-Day Completion" 
          value={`${data.wccr_30d}%`}
          icon={Activity}
          color="blue"
        />
        <MetricCard 
          title="Closes Started" 
          value={data.started_count}
          subtitle="Last 30 days"
          icon={Calendar}
          color="purple"
        />
        <MetricCard 
          title="Closes Finalized" 
          value={data.finalized_count}
          subtitle="Last 30 days"
          icon={CheckCircle2}
          color="emerald"
        />
      </div>
      
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Completion Funnel</CardTitle>
          <CardDescription>Dropoff at each stage</CardDescription>
        </CardHeader>
        <CardContent>
          <FunnelBar stages={data.dropoff} total={data.started_count} />
        </CardContent>
      </Card>
    </div>
  );
}

function InterventionEffectiveness({ data }) {
  if (!data) return null;
  
  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        <MetricCard 
          title="Patterns Detected" 
          value={data.closes_with_patterns}
          subtitle="Closes with interventions"
          icon={Target}
          color="amber"
        />
        <MetricCard 
          title="Selection Rate" 
          value={`${data.intervention_selected_rate}%`}
          subtitle="Selected an action"
          icon={CheckCircle2}
          color="emerald"
        />
        <MetricCard 
          title="Top Intervention" 
          value={data.most_selected_intervention || "None"}
          subtitle="Most selected action"
          icon={TrendingUp}
          color="blue"
        />
      </div>
    </div>
  );
}

function GoalAdoption({ data }) {
  if (!data) return null;
  
  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <MetricCard 
          title="Households with Goals" 
          value={`${data.households_with_goals_rate}%`}
          icon={Target}
          color="emerald"
        />
        <MetricCard 
          title="Avg Goals/Household" 
          value={data.avg_goals_per_household.toFixed(1)}
          icon={BarChart3}
          color="blue"
        />
        <MetricCard 
          title="Goals Achieved" 
          value={`${data.goals_achieved_rate}%`}
          icon={CheckCircle2}
          color="purple"
        />
        <MetricCard 
          title="Stagnation Rate" 
          value={`${data.stagnation_rate}%`}
          subtitle="No update in 14+ days"
          icon={Clock}
          color="amber"
        />
      </div>
    </div>
  );
}

function StabilitySignals({ data }) {
  if (!data) return null;
  
  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-2">
        <MetricCard 
          title="3+ Week Rate" 
          value={`${data.three_plus_week_rate}%`}
          subtitle="Consistent engagement"
          icon={TrendingUp}
          color="emerald"
        />
        <MetricCard 
          title="Week Override Rate" 
          value={`${data.week_override_rate}%`}
          subtitle="Manual week selection"
          icon={Calendar}
          color="gray"
        />
      </div>
      
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Consecutive Close Distribution</CardTitle>
          <CardDescription>Weeks of continuous engagement</CardDescription>
        </CardHeader>
        <CardContent>
          <DistributionBar distribution={data.consecutive_close_distribution} />
        </CardContent>
      </Card>
    </div>
  );
}

function OnboardingFriction({ data }) {
  if (!data) return null;
  
  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-2">
        <MetricCard 
          title="Signup to Close Rate" 
          value={`${data.signup_to_first_close_rate}%`}
          subtitle="Conversion to first close"
          icon={ArrowRight}
          color="emerald"
        />
        <MetricCard 
          title="Days to First Close" 
          value={data.median_days_to_first_finalize || "N/A"}
          subtitle="Median days"
          icon={Clock}
          color="blue"
        />
      </div>
    </div>
  );
}

// ============== Main Dashboard ==============

export default function FounderDashboardPage() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [activeTab, setActiveTab] = useState("weekly-close");
  
  const fetchDashboard = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.get("/api/admin/dashboard/summary");
      
      if (!response.ok) {
        if (response.status === 403) {
          setError("Admin access required. This dashboard is for founders only.");
        } else {
          setError("Unable to load dashboard data");
        }
        toast.error("Dashboard access issue");
        return;
      }
      
      const jsonData = await response.json();
      setData(jsonData);
    } catch (err) {
      setError("Unable to load dashboard data");
      toast.error("Dashboard access issue");
    } finally {
      setLoading(false);
    }
  }, []);
  
  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);
  
  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      </AppLayout>
    );
  }
  
  if (error) {
    return (
      <AppLayout>
        <div className="flex flex-col items-center justify-center h-64 space-y-4">
          <Shield className="w-12 h-12 text-muted-foreground" />
          <p className="text-muted-foreground text-center max-w-md">{error}</p>
        </div>
      </AppLayout>
    );
  }
  
  return (
    <AppLayout>
      <div className="space-y-6" data-testid="founder-dashboard">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Founder Dashboard</h1>
            <p className="text-muted-foreground">
              Behavioral stability metrics (30-day window)
            </p>
          </div>
          <Badge variant="outline" className="text-xs">
            Admin Only
          </Badge>
        </div>
        
        {/* Last Updated */}
        {data?.generated_at && (
          <p className="text-xs text-muted-foreground">
            Last updated: {new Date(data.generated_at).toLocaleString()}
          </p>
        )}
        
        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid grid-cols-5 w-full max-w-2xl">
            <TabsTrigger value="weekly-close" data-testid="tab-weekly-close">
              Weekly Reset            </TabsTrigger>
            <TabsTrigger value="interventions" data-testid="tab-interventions">
              Interventions
            </TabsTrigger>
            <TabsTrigger value="goals" data-testid="tab-goals">
              Goals
            </TabsTrigger>
            <TabsTrigger value="stability" data-testid="tab-stability">
              Stability
            </TabsTrigger>
            <TabsTrigger value="onboarding" data-testid="tab-onboarding">
              Onboarding
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="weekly-close" className="mt-6">
            <WeeklyCloseHealth data={data?.weekly_close} />
          </TabsContent>
          
          <TabsContent value="interventions" className="mt-6">
            <InterventionEffectiveness data={data?.interventions} />
          </TabsContent>
          
          <TabsContent value="goals" className="mt-6">
            <GoalAdoption data={data?.goals} />
          </TabsContent>
          
          <TabsContent value="stability" className="mt-6">
            <StabilitySignals data={data?.stability} />
          </TabsContent>
          
          <TabsContent value="onboarding" className="mt-6">
            <OnboardingFriction data={data?.onboarding} />
          </TabsContent>
        </Tabs>
      </div>
    </AppLayout>
  );
}
